<div class="sr-wrap" cdkDropListGroup>
  <div class="sr-columns">
    <!-- Palette: the rule types, dragged onto a gate to open its config modal. -->
    <aside class="sr-palette-col">
      <header class="sr-col-head">
        <span class="sr-col-title">Entry rules</span>
        <span class="sr-col-meta">{{ ruleTypes.length }}</span>
      </header>
      <p class="sr-col-hint">
        Drag a rule type onto a gate to configure it. Events and Access Groups decide which
        tickets scan in; Section &amp; Row, Ticket Usage and Price Code each seed a test row.
      </p>
      <div class="sr-droplist sr-palette"
           cdkDropList
           [cdkDropListData]="ruleTypes"
           [cdkDropListSortingDisabled]="true">
        <div class="sr-rule-chip"
             *ngFor="let type of ruleTypes; trackBy: trackRuleType"
             [class]="'sr-rule-chip sr-rule-chip--' + type"
             cdkDrag
             [cdkDragData]="type">
          <span class="sr-rule-chip-icon" aria-hidden="true">{{ ruleTypeIcon(type) }}</span>
          <span class="sr-rule-chip-label">{{ ruleTypeLabel(type) }}</span>
          <span class="sr-rule-chip-handle" aria-hidden="true">⋮⋮</span>
        </div>
      </div>

      <button type="button"
              class="sr-manage-groups"
              [disabled]="saving"
              (click)="openAccessGroupManager()">Manage access groups</button>
    </aside>

    <!-- One card per gate; each is a drop target for rule-type chips. -->
    <div class="sr-gates-col">
      <div class="sr-gates-toolbar" *ngIf="gates.length">
        <button type="button" class="sr-collapse-all" (click)="collapseAll(true)">Collapse all</button>
        <button type="button" class="sr-collapse-all" (click)="collapseAll(false)">Expand all</button>
      </div>

      <div class="sr-gate" *ngFor="let gate of gates; trackBy: trackGate">
        <header class="sr-gate-head"
                (click)="toggleGate(gate)"
                [class.sr-gate-head--collapsed]="gate.collapsed">
          <span class="sr-gate-toggle" aria-hidden="true">{{ gate.collapsed ? '▸' : '▾' }}</span>
          <span class="sr-gate-name">{{ gate.gate }}</span>
          <span class="sr-gate-summary" *ngIf="gate.collapsed">
            {{ gateRuleCount(gate) }} rule{{ gateRuleCount(gate) === 1 ? '' : 's' }}
            <ng-container *ngIf="gate.groups.length > 1">in {{ gate.groups.length }} sets</ng-container>
          </span>
          <div class="sr-gate-actions">
            <button type="button"
                    class="sr-gate-action"
                    appTooltip="Add every event and plan to this gate"
                    [disabled]="saving || !events.length"
                    (click)="$event.stopPropagation(); acceptAllEvents(gate)">Accept all</button>
            <button type="button"
                    class="sr-gate-action"
                    appTooltip="Remove all rules — this gate rejects everything"
                    [disabled]="saving || !gateHasRules(gate)"
                    (click)="$event.stopPropagation(); clearGate(gate)">Clear</button>
            <button type="button"
                    class="sr-gate-action"
                    *ngIf="gateGroupFor(gate) as group"
                    [appTooltip]="'Copy these rules to every gate in ' + group.name"
                    [disabled]="saving"
                    (click)="$event.stopPropagation(); copyToGroup(gate)">Copy to {{ group.name }}</button>
          </div>
        </header>

        <div class="sr-gate-body" *ngIf="!gate.collapsed">
          <!-- Each group is one AND set (rules joined by "+"); groups are ORed. -->
          <ng-container *ngFor="let group of gate.groups; let gi = index; trackBy: trackGroup">
            <div class="sr-or-divider" *ngIf="gi > 0"><span>OR</span></div>

            <div class="sr-group">
              <button type="button"
                      class="sr-group-remove"
                      *ngIf="gate.groups.length > 1"
                      appTooltip="Remove this rule set"
                      [disabled]="saving"
                      (click)="removeGroup(gate, group)">✕</button>

              <div class="sr-droplist sr-rules-drop"
                   cdkDropList
                   [cdkDropListData]="group.rules"
                   (cdkDropListDropped)="onDropRuleType($event, group)">
                <div class="sr-rule-card"
                     *ngFor="let dr of group.rules; trackBy: trackDraftRule"
                     [class]="'sr-rule-card sr-rule-card--' + dr.rule.type"
                     (click)="editRule(group, dr)">
                  <span class="sr-rule-card-icon" aria-hidden="true">{{ ruleTypeIcon(dr.rule.type) }}</span>
                  <span class="sr-rule-card-text">
                    <span class="sr-rule-card-type">{{ ruleTypeLabel(dr.rule.type) }}</span>
                    <span class="sr-rule-card-summary">{{ ruleSummary(dr.rule) }}</span>
                  </span>
                  <button type="button"
                          class="sr-rule-remove"
                          appTooltip="Remove this rule"
                          [disabled]="saving"
                          (click)="$event.stopPropagation(); removeRule(gate, group, dr)">✕</button>
                </div>
                <div class="sr-empty" *ngIf="!group.rules.length">
                  Drop rules here — rules in a set are combined with “+”.
                </div>
              </div>
            </div>
          </ng-container>

          <button type="button"
                  class="sr-add-group"
                  [disabled]="saving"
                  appTooltip="Add another rule set — sets are combined with OR"
                  (click)="addGroup(gate)">
            + Add rule set (OR)
          </button>
        </div>
      </div>

      <div class="sr-empty sr-empty--gates" *ngIf="!gates.length">
        No gates configured for this performance.
      </div>
    </div>
  </div>

  <footer class="sr-foot">
    <p class="sr-error" *ngIf="error">{{ error }}</p>
    <div class="sr-foot-actions">
      <button type="button"
              class="sr-btn sr-btn-cancel"
              [disabled]="saving"
              (click)="onCancel()">Cancel</button>
      <button type="button"
              class="sr-btn sr-btn-save"
              [disabled]="saving"
              (click)="onSave()">
        <span class="sr-btn-icon" aria-hidden="true">💾</span>
        {{ saving ? 'Saving…' : 'Save rules' }}
      </button>
    </div>
  </footer>
</div>

<!-- Config modals: shown for a pending drop or an active edit of that type. -->
<app-sr-scan-period-modal
  *ngIf="isModalOpen('scan_period')"
  [initial]="editingScanPeriod()"
  (confirmed)="confirmScanPeriod($event)"
  (cancelled)="cancelRuleModal()"></app-sr-scan-period-modal>

<app-sr-events-modal
  *ngIf="isModalOpen('events')"
  [initial]="editingEvents()"
  [events]="events"
  (confirmed)="confirmEventsRule($event)"
  (cancelled)="cancelRuleModal()"></app-sr-events-modal>

<app-sr-access-group-modal
  *ngIf="isModalOpen('access_group')"
  [initial]="editingAccessGroup()"
  [accessGroups]="accessGroups"
  (confirmed)="confirmAccessGroupRule($event)"
  (cancelled)="cancelRuleModal()"
  (manageGroups)="cancelRuleModal(); openAccessGroupManager()"></app-sr-access-group-modal>

<app-sr-section-row-modal
  *ngIf="isModalOpen('section_row')"
  [initial]="editingSectionRow()"
  (confirmed)="confirmSectionRowRule($event)"
  (cancelled)="cancelRuleModal()"></app-sr-section-row-modal>

<app-sr-ticket-usage-modal
  *ngIf="isModalOpen('ticket_usage')"
  [initial]="editingTicketUsage()"
  (confirmed)="confirmTicketUsageRule($event)"
  (cancelled)="cancelRuleModal()"></app-sr-ticket-usage-modal>

<app-sr-price-code-modal
  *ngIf="isModalOpen('price_code')"
  [initial]="editingPriceCode()"
  [priceCodes]="priceCodes"
  (confirmed)="confirmPriceCodeRule($event)"
  (cancelled)="cancelRuleModal()"></app-sr-price-code-modal>

<app-sr-access-group-manager
  *ngIf="showAccessGroupManager"
  [accessGroups]="accessGroups"
  [events]="events"
  (save)="onSaveAccessGroups($event)"
  (cancel)="closeAccessGroupManager()"></app-sr-access-group-manager>
